import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TelehealthClassEnum, TelehealthType } from 'app/shared/models/surveyData.model';

export interface ConsultationSummaryData {
    telehealthType: TelehealthType;
    orderId: number;
}

export interface ConsultationSummaryResult {
    clinicianInstructions: string;
    diagnosis: string | null;
    telehealthType: TelehealthType;
    orderId: number;
    timestamp: string;
}

// Custom validator for minimum alphanumeric content
function minAlphanumericValidator(minLength: number = 5) {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null; // Let required validator handle empty values
        }

        const rawContent = String(control.value);

        // Remove HTML tags first, then get plain text
        const plainText = rawContent.replace(/<[^>]*>/g, '');

        // Remove all whitespace and non-alphanumeric characters for counting
        const alphanumericOnly = plainText.replace(/[^a-zA-Z0-9]/g, '');

        // Check if the field contains only HTML tags and/or whitespace (no actual content)
        if (plainText.trim().length === 0) {
            return { 'whitespaceOnly': true };
        }

        // Check if we have minimum alphanumeric characters
        if (alphanumericOnly.length < minLength) {
            return { 'minAlphanumeric': { requiredLength: minLength, actualLength: alphanumericOnly.length } };
        }

        return null;
    };
}

@Component({
    selector: 'app-dialog-consultation-summary',
    templateUrl: './dialog-consultation-summary.component.html',
    styleUrls: ['./dialog-consultation-summary.component.scss'],
    standalone: false
})
export class DialogConsultationSummaryComponent implements OnInit {
    consultationForm: UntypedFormGroup;
    isExpressConsult: boolean = false;
    tooltipText: string = `This is your opportunity to <strong>summarise your advice</strong>, care instructions, or next steps for the patient.<br><br><em>Keep it clear and patient-friendly</em> — it will be emailed to them after the consult.`;
    // Quill editor configurations
    quillConfig = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            // [{ 'font': [] }],
            // [{ 'size': ['small', false, 'large', 'huge'] }],
            // [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            // [{ 'indent': '-1' }, { 'indent': '+1' }],
            // [{ 'align': [] }],
            // ['blockquote', 'code-block'],
            // [{ 'script': 'sub' }, { 'script': 'super' }],
            // ['clean']
        ]
    };

    constructor(
        public dialogRef: MatDialogRef<DialogConsultationSummaryComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConsultationSummaryData,
        private formBuilder: UntypedFormBuilder
    ) {
        this.initializeForm();
    }

    ngOnInit(): void {
        this.checkConsultationType();
    }

    private initializeForm(): void {
        this.consultationForm = this.formBuilder.group({
            clinicianInstructions: ['', [Validators.required, minAlphanumericValidator(5)]],
            diagnosis: ['']
        });
    }

    private checkConsultationType(): void {
        this.isExpressConsult = this.data.telehealthType === TelehealthClassEnum.SYMPTOM_REQUEST;

        if (this.isExpressConsult) {
            this.consultationForm.get('diagnosis')?.setValidators([Validators.required, minAlphanumericValidator(5)]);
            this.consultationForm.get('diagnosis')?.updateValueAndValidity();
        }
    }

    onSubmit(): void {
        if (this.consultationForm.valid) {
            const formData = this.consultationForm.value;

            const result: ConsultationSummaryResult = {
                clinicianInstructions: formData.clinicianInstructions || '',
                diagnosis: this.isExpressConsult ? (formData.diagnosis || '') : null,
                telehealthType: this.data.telehealthType,
                orderId: this.data.orderId,
                timestamp: new Date().toISOString()
            };

            this.dialogRef.close(result);
        } else {
            this.consultationForm.markAllAsTouched();
        }
    }

    onCancel(): void {
        this.dialogRef.close(null);
    }

    hasError(fieldName: string, errorType: string): boolean {
        const field = this.consultationForm.get(fieldName);
        return field ? field.hasError(errorType) && (field.dirty || field.touched) : false;
    }

    getErrorMessage(fieldName: string): string {
        const field = this.consultationForm.get(fieldName);
        const isInstructions = fieldName === 'clinicianInstructions';

        if (field?.hasError('required')) {
            return isInstructions
                ? 'Please provide instructions for your patient'
                : 'Please enter a diagnosis for this consultation';
        }

        if (field?.hasError('whitespaceOnly')) {
            return isInstructions
                ? 'Instructions cannot be empty or contain only spaces'
                : 'Diagnosis cannot be empty or contain only spaces';
        }

        if (field?.hasError('minAlphanumeric')) {
            const error = field.getError('minAlphanumeric');
            return isInstructions
                ? `Please provide more detailed instructions (minimum ${error.requiredLength} characters)`
                : `Please provide a more detailed diagnosis (minimum ${error.requiredLength} characters)`;
        }

        return '';
    }

    getCharacterCount(fieldName: string): number {
        const field = this.consultationForm.get(fieldName);
        const rawContent = field?.value ? String(field.value) : '';

        // Remove HTML tags first, then count alphanumeric characters
        const plainText = rawContent.replace(/<[^>]*>/g, '');
        const alphanumericOnly = plainText.replace(/[^a-zA-Z0-9]/g, '');

        return alphanumericOnly.length;
    }
}
